import React from "react";
import PropTypes from "prop-types";
import {
  Button,
  Icon,
  Checkbox,
  Table,
  Header,
  Input,
  Label,
} from "semantic-ui-react";

const BookSelectionForm = ({
  selectedPatron,
  selectedBooks,
  isOverrideMode,
  overrideAmounts,
  originalCalculatedAmounts,
  useManualTotal,
  onToggleOverrideMode,
  onBookSelection,
  onOverrideAmountChange,
  onSelectAll,
  onSelectNone,
  formatDate,
  allowOverride,
}) => {
  if (!selectedPatron || selectedPatron.loans.length === 0) {
    return null;
  }

  return (
    <>
      <Header as="h4">
        Select Books for Payment
        {allowOverride && (
          <Button
            floated="right"
            size="mini"
            color={isOverrideMode ? "red" : "blue"}
            onClick={onToggleOverrideMode}
          >
            <Icon name={isOverrideMode ? "cancel" : "edit"} />
            {isOverrideMode ? "Cancel" : "Override Amounts"}
          </Button>
        )}
      </Header>

      {selectedPatron.loans.length > 1 && (
        <div className="selection-buttons">
          <Button size="small" onClick={onSelectAll}>
            Select All
          </Button>
          <Button size="small" onClick={onSelectNone}>
            Select None
          </Button>
        </div>
      )}

      <Table celled>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell width={1}>Select</Table.HeaderCell>
            <Table.HeaderCell>Book Title</Table.HeaderCell>
            <Table.HeaderCell width={2}>Due Date</Table.HeaderCell>
            <Table.HeaderCell width={2}>
              {isOverrideMode ? "Override Amount" : "Fine Amount"}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {selectedPatron.loans.map((loan, index) => {
            const currentAmount =
              parseFloat(overrideAmounts[index]) >= 0
                ? parseFloat(overrideAmounts[index])
                : loan.calculatedFine;

            // Get the original calculated amount
            const originalAmount =
              originalCalculatedAmounts?.[index] !== undefined
                ? originalCalculatedAmounts[index]
                : loan.calculatedFine;

            // Check if amount was overridden
            const isOverridden = currentAmount !== originalAmount;

            return (
              <Table.Row key={index}>
                <Table.Cell>
                  <Checkbox
                    checked={selectedBooks.has(index)}
                    onChange={(e, { checked }) =>
                      onBookSelection(index, checked)
                    }
                  />
                </Table.Cell>
                <Table.Cell>
                  <strong>{loan.metadata.document.title}</strong>
                  {loan.metadata.document.edition && (
                    <div className="book-edition">
                      {loan.metadata.document.edition}
                    </div>
                  )}
                </Table.Cell>
                <Table.Cell>
                  <span className="overdue-date">
                    {formatDate(loan.metadata.end_date)}
                  </span>
                </Table.Cell>
                <Table.Cell>
                  {isOverrideMode && !useManualTotal && allowOverride ? (
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={overrideAmounts[index] || ""}
                      onChange={(e) =>
                        onOverrideAmountChange(index, e.target.value)
                      }
                      label={{ basic: true, content: "₱" }}
                      labelPosition="left"
                      size="mini"
                      placeholder="0.00"
                    />
                  ) : (
                    <div>
                      <strong className="fine-amount">
                        ₱{currentAmount.toFixed(2)}
                      </strong>

                      {/* Show OVERRIDDEN label and original amount when overridden */}
                      {isOverridden && (
                        <span style={{ marginLeft: "8px" }}>
                          <Label size="mini" color="orange">
                            OVERRIDDEN
                          </Label>
                          <span
                            style={{
                              fontSize: "0.85em",
                              color: "#666",
                              marginLeft: "4px",
                            }}
                          >
                            (was ₱{originalAmount.toFixed(2)})
                          </span>
                        </span>
                      )}

                      {/* Show waived label when amount is explicitly 0 and was overridden */}
                      {currentAmount === 0 && isOverridden && (
                        <Label
                          size="mini"
                          color="green"
                          style={{ marginLeft: "8px" }}
                        >
                          WAIVED
                        </Label>
                      )}
                    </div>
                  )}
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table>
    </>
  );
};

BookSelectionForm.propTypes = {
  selectedPatron: PropTypes.object,
  selectedBooks: PropTypes.instanceOf(Set).isRequired,
  isOverrideMode: PropTypes.bool.isRequired,
  overrideAmounts: PropTypes.object.isRequired,
  originalCalculatedAmounts: PropTypes.object,
  useManualTotal: PropTypes.bool.isRequired,
  onToggleOverrideMode: PropTypes.func.isRequired,
  onBookSelection: PropTypes.func.isRequired,
  onOverrideAmountChange: PropTypes.func.isRequired,
  onSelectAll: PropTypes.func.isRequired,
  onSelectNone: PropTypes.func.isRequired,
  formatDate: PropTypes.func.isRequired,
  allowOverride: PropTypes.bool,
};

BookSelectionForm.defaultProps = {
  selectedPatron: null,
  originalCalculatedAmounts: {},
  allowOverride: true,
};

export default BookSelectionForm;
